import React from 'react';
import {
  Paper,
  Grid,
  Typography,
  Box,
  alpha,
  useTheme,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Cell
} from 'recharts';

const RelatorioServicos = ({ dados, customTheme }) => {
  const theme = useTheme();

  // Cores para os gráficos
  const cores = [
    customTheme.palette.primary.main,
    '#4caf50',
    '#ff9800',
    '#f44336',
    '#9c27b0',
    '#2196f3'
  ];

  // Verificar se temos dados de serviços
  const servicosEstatisticas = dados?.servicosEstatisticas || [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  if (!dados || !servicosEstatisticas.length) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        Nenhum dado de serviços disponível para o período selecionado.
      </Alert>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Cards de Desempenho por Serviço */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Desempenho por Serviço
        </Typography>
        
        <Grid container spacing={3}>
          {dados.servicosEstatisticas.map((servico, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(servico.cor, 0.2)}`,
                  background: `linear-gradient(135deg, ${alpha(servico.cor, 0.05)} 0%, ${alpha(servico.cor, 0.02)} 100%)`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px ${alpha(servico.cor, 0.2)}`,
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: servico.cor }}>
                    {servico.nome}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Volume Total
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: servico.cor }}>
                      {servico.total}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {servico.ativo ? 'Ativo' : 'Inativo'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Média
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {servico.media || 0}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Descrição
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 400 }}>
                      {servico.descricao || 'N/A'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Gráfico de Barras Comparativo */}
      <Grid item xs={12}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
            height: 400
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Comparativo de Volume por Serviço
          </Typography>
          
          <ResponsiveContainer width="100%" height="85%">
            <BarChart data={servicosEstatisticas}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={alpha(theme.palette.text.primary, 0.1)}
              />
              <XAxis 
                dataKey="nome"
                stroke={theme.palette.text.secondary}
                fontSize={12}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="total" 
                fill={customTheme.palette.primary.main}
                radius={[8, 8, 0, 0]}
              >
                {servicosEstatisticas.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={cores[index % cores.length]} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>


    </Grid>
  );
};

export default RelatorioServicos; 